import * as React from 'react';
import globeIcon from '@/assets/images/svg/globe_icon.svg';
import privateIcon from '@/assets/images/svg/private_icon.svg';
import { VisibilityCard } from './visibility/VisibilityCard';
import { SectionHeader } from './shared/SectionHeader';
import { Button } from '@/components/ui/Button';
import { StripeGateModal } from '@/features/account/components/StripeGateModal';
import { useStripePublicGate } from '@/features/account/hooks/useStripePublicGate';
import { useStripeOnboarding } from '../hooks/useStripeOnboarding';

interface VisibilitySectionProps {
  isPublic: boolean;
  onUpdate: (isPublic: boolean) => void;
  disabled?: boolean;
}

export const VisibilitySection: React.FC<VisibilitySectionProps> = ({
  isPublic,
  onUpdate,
  disabled,
}) => {
  const { handleStartMonetize, isProcessing, stripeButtonLabel } = useStripeOnboarding();
  const goPublic = React.useCallback(() => onUpdate(true), [onUpdate]);
  const { trigger: gatePublic, isWarningOpen, closeWarning } = useStripePublicGate(goPublic);

  const handlePublicClick = () => {
    if (disabled) return;
    gatePublic();
  };

  return (
    <div className='flex w-full flex-col gap-6'>
      <div className='flex flex-col gap-2'>
        {/* Header Row: MONETIZE above title on <lg (full-width), side-by-side on lg+ */}
        <div className='flex flex-col-reverse gap-3 lg:flex-row lg:items-center lg:justify-between lg:gap-4'>
          <SectionHeader title='Choose Echo Visibility' />
          <Button
            variant='purple-dark'
            className='h-[48px] w-full rounded-30 text-14 font-bold uppercase lg:h-[39px] lg:w-auto lg:px-12'
            onClick={handleStartMonetize}
            disabled={isProcessing}
          >
            {isProcessing ? 'PROCESSING...' : stripeButtonLabel}
          </Button>
        </div>

        {/* Section Subtitle */}
        <p className='font-sans text-16 font-normal leading-24 tracking-normal text-gray-600'>
          Select how you want your digital echo to be accessible to others.
        </p>
      </div>

      <div className='grid grid-cols-1 gap-6 lg:grid-cols-2'>
        <VisibilityCard
          title='Public Echo'
          subtitle='All your fans can see your digital echo, they can find it, chat with it, and pay to subscribe for exclusive conversations with your echo.'
          icon={<img src={globeIcon} alt='Public' className='size-full object-contain' />}
          isSelected={isPublic}
          onClick={handlePublicClick}
          disabled={!!disabled}
        />

        <VisibilityCard
          title='Private Echo'
          subtitle='Your echo stays private, only you can see it.'
          icon={<img src={privateIcon} alt='Private' className='size-full object-contain' />}
          isSelected={!isPublic}
          onClick={() => {
            if (!disabled) onUpdate(false);
          }}
          disabled={!!disabled}
        />
      </div>

      <StripeGateModal isOpen={isWarningOpen} onClose={closeWarning} onProceed={goPublic} />
    </div>
  );
};
